/**
 * Houseplant v2 — Legal contents list
 *
 * Drives the "Quick Links" rail in sections/hp-legal.liquid. Two jobs:
 *
 *   1. BUILD the list from the <h2>s actually in the document body, rather than
 *      from typed-in blocks that could drift from the headings they name. The
 *      body is a merchant's rich text — a pasted privacy policy — so the
 *      headings are the only thing that can be trusted to be current.
 *   2. TRACK which section is being read, and mark it.
 *
 * ── Second contents list in the theme, and deliberately NOT the same file ────
 * assets/hp-faq-toc.js does the same two jobs for the FAQ page. The convention
 * is note at the second, extract at the third — this is the note. When a third
 * appears, the shared part is worth pulling out, but check what is actually
 * shared first, because the two differ in more than their selectors:
 *
 *   · The FAQ list is a SIBLING of the sections it describes and cannot wrap
 *     them, so most of that file is the measure-the-span-and-cancel-it-with-a-
 *     negative-margin trick that gives `position: sticky` a containing block to
 *     travel inside. Here the rail and the body are two columns of ONE flex row,
 *     so sticky works with no help at all and none of that code exists.
 *   · The FAQ targets whole sections; this targets headings inside one rich-text
 *     blob, which also means MINTING IDS — a merchant's HTML has none.
 *
 * What would genuinely extract is the active-section tracking, which is the
 * smaller half.
 *
 * Everything here is enhancement. With the script absent the rail is empty and
 * removes itself, and the page reads as an ordinary document.
 */
class HpLegalToc extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-hp-legal-toc-list]');
    this.body = document.querySelector(this.dataset.content || '[data-hp-legal-content]');
    if (!this.list || !this.body) return this.remove();

    this.promoteBoldParagraphs();
    this.headings = Array.from(this.body.querySelectorAll('h2'));

    /* One heading needs no contents list, and none certainly doesn't. Removing
       the element is better than leaving an empty rail holding the gutter open. */
    if (this.headings.length < 2) return this.remove();

    this.build();
    this.track();
  }

  /**
   * WORD-PASTE RESCUE, and explicitly a fallback rather than the design.
   *
   * The live Privacy Policy has ZERO headings: 44 sibling <p>s where every
   * section title is a paragraph whose entire text is bold ("Information
   * Collection", "Information Use", …). That is what counsel's Word document
   * turns into when it is pasted into the Shopify editor, and it will happen
   * again on every revision. Left alone it gives a wall of undifferentiated
   * copy and no contents list at all — which is worse for a legal document than
   * for almost any other page, and is a real accessibility problem besides.
   *
   * So a fully-bold paragraph is promoted to a real <h2>, which then feeds both
   * the styling and the list below. The test is deliberately narrow, because
   * the cost of a false positive is a sentence rendered at 44px:
   *
   *   · the document must have NO real <h2> anywhere. A properly marked-up
   *     policy is left completely alone, and a bold run inside it stays
   *     emphasis.
   *   · the bold element must cover the WHOLE paragraph, not a phrase in it.
   *   · it must be short. A long bold paragraph is a shouted disclaimer — every
   *     policy has one — not a section title.
   *
   * The right fix is still to mark the headings up as Heading 2 in the page
   * editor; this keeps the page working until someone does, and switches off
   * with `data-promote-bold="false"`.
   */
  promoteBoldParagraphs() {
    if (this.dataset.promoteBold === 'false') return;
    if (this.body.querySelector('h2')) return;

    this.body.querySelectorAll(':scope > p').forEach((p) => {
      const text = p.textContent.trim();
      if (!text || text.length > 80) return;

      const bold = p.querySelector('b, strong');
      if (!bold || bold.textContent.trim().length < text.length) return;

      const heading = document.createElement('h2');
      heading.textContent = text;
      /* Marked so the stylesheet can say so, and so anyone inspecting the page
         can see this heading was inferred rather than authored. */
      heading.dataset.hpPromoted = 'true';
      p.replaceWith(heading);
    });
  }

  build() {
    const frag = document.createDocumentFragment();

    this.links = this.headings.map((heading, i) => {
      /* A merchant's pasted HTML has no ids, and any it does have are theirs to
         keep — only mint one when it's missing. Namespaced and indexed so two
         headings with the same words can't collide. */
      if (!heading.id) heading.id = `${this.id || 'HpLegal'}-section-${i + 1}`;

      const item = document.createElement('li');
      item.className = 'hp-legal__toc-item';

      const link = document.createElement('a');
      /* The type class is NOT optional. Without it the link inherits base.css's
         body leading (1.8 → 28.8 on a 16px link) and every row comes out 4.8
         taller than the node's 24, which the row's own min-height cannot pull
         back. Built in JS, so it has to be spelled here rather than in Liquid. */
      link.className = 'hp-legal__toc-link hp-text-body-l-reg';
      link.href = `#${heading.id}`;
      /* textContent, not innerHTML: the heading may carry <em>/<strong>/<a>
         from the rich text, and none of that belongs in a contents entry. */
      link.textContent = heading.textContent.trim();

      item.appendChild(link);
      frag.appendChild(item);
      return link;
    });

    this.list.appendChild(frag);
  }

  track() {
    /* The observer is only a cheap "something moved" trigger — the decision is
       made by re-reading every heading's position, because "which section am I
       in" is about the LAST heading above the line, and an entry that just left
       the viewport carries no information about the ones that didn't move.
       Reading rects for a dozen headings on an intersection is nothing. */
    const update = () => {
      /* A quarter down the viewport: high enough that the heading you just
         scrolled to counts immediately, low enough that the one above doesn't
         hold on while its section is off screen. */
      const line = window.innerHeight * 0.25;
      let active = 0;

      this.headings.forEach((heading, i) => {
        if (heading.getBoundingClientRect().top <= line) active = i;
      });

      this.links.forEach((link, i) => {
        if (i === active) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    };

    this.observer = new IntersectionObserver(update, {
      threshold: [0, 1],
      rootMargin: '0px 0px -75% 0px',
    });
    this.headings.forEach((heading) => this.observer.observe(heading));

    /* The observer fires on setup, but only for headings that intersect. A page
       loaded at an anchor deep in the document needs the first pass anyway. */
    update();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }
}

customElements.define('hp-legal-toc', HpLegalToc);
